/**
 * Source Attribution Script
 * Tracks new visitors and captures source information including:
 * - Current URI
 * - Referrer
 * - UTM parameters
 * - Source and medium (similar to Google Analytics)
 * - Session management
 */

// Session timeout in milliseconds (30 minutes)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Paid traffic identifiers and their sources
const PAID_IDENTIFIERS = {
  'gclid': 'Google',
  'msclkid': 'Microsoft',
  'fbclid': 'Facebook'
};

/**
 * Manages user sessions and source attribution
 * - Detects new sessions (30+ min since last activity)
 * - Captures source attribution for new visitors
 * - Re-captures attribution when UTM params or paid identifiers are present
 */
export function manageSessions() {
  const currentTime = new Date().getTime();
  const lastActivity = localStorage.getItem('gam-last-activity');
  const hasUtmOrPaidParams = checkForUtmOrPaidParams();
  
  // Check if this is a new session
  const parsedLastActivity = parseInt(lastActivity, 10);
  const diff = currentTime - parsedLastActivity;
  const isNewSession = !lastActivity || (currentTime - parseInt(lastActivity, 10)) > SESSION_TIMEOUT;
  
  // Update last activity time
  localStorage.setItem('gam-last-activity', currentTime.toString());
  
  // Capture source attribution if:
  // 1. It's a new session, OR
  // 2. UTM parameters or paid identifiers are present in the URL
  if (isNewSession || hasUtmOrPaidParams) {
    // Capture source attribution
    const sourceData = captureSourceAttribution(true);
    
    // If it's a new session, increment session count
    if (isNewSession) {
      const sessionCount = localStorage.getItem('gam-session-count') || 0;
      localStorage.setItem('gam-session-count', parseInt(sessionCount, 10) + 1);
    }
    
    return {
      isNewSession,
      hasNewSourceData: !!sourceData,
      sourceData
    };
  } else {

    // need to check if gambino player status has changed
    // if so, update the sourceData
    const playerStatus = getPlayerStatus();
    let sourceData = localStorage.getItem('gam-source') || '';
    if(sourceData) sourceData = JSON.parse(sourceData);
    if(sourceData && sourceData.playerStatus && JSON.stringify(playerStatus) !== sourceData.playerStatus) {
      sourceData.playerStatus = playerStatus;
      localStorage.setItem('gam-source', JSON.stringify(sourceData));
    }

    return {
      isNewSession,
      hasNewSourceData: false,
      sourceData: sourceData
    };
  }

}

function getPlayerStatus() {
  // read from local storage or apply defaults
  // existing storage items are written here: C:\dev\gam\lp\js\spiraltoken-v1.0.js
  // loaded in fun_free_games.html via: <script type="text/javascript" src="../js/spiraltoken-v1.0.js"></script>
  // localStorage.setItem("externalUserId", JSON.stringify(externalUserId)); // "6350371637821440"
  // spiralToken = JSON.parse(localStorage.getItem("gambino_spiral_token")); // "0c1af8a7-96cb-43c2-8ec6-e99e619560a5"
  // loginDate = JSON.parse(localStorage.getItem("gambino_login_date")); // "2025-04-02T09:30:46.775Z"
  let userId = localStorage.getItem("externalUserId");
  let spiralToken = localStorage.getItem("gambino_spiral_token");
  let loginDate = localStorage.getItem("gambino_login_date");
  return {
    userId: userId ? JSON.parse(userId) : 0,
    spiralToken: spiralToken ? JSON.parse(spiralToken) : '',
    loginDate: loginDate ? JSON.parse(loginDate) : ''
  }
}

/**
 * Checks if URL contains UTM parameters or paid traffic identifiers
 * @returns {boolean} True if UTM params or paid identifiers exist
 */
function checkForUtmOrPaidParams() {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check for UTM parameters
  for (const [key] of urlParams.entries()) {
    if (key.startsWith('utm_')) {
      return true;
    }
  }
  
  // Check for paid identifiers
  for (const param of Object.keys(PAID_IDENTIFIERS)) {
    if (urlParams.has(param)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Captures source attribution data for new visitors
 * @param {boolean} forceCapture - Force capture even if data already exists
 * @returns {Object|null} The source data object if captured, null otherwise
 */
export function captureSourceAttribution(forceCapture = false) {
  // Check if user already has source attribution data and we're not forcing a capture
  if (!forceCapture && localStorage.getItem('gam-source')) {
    return null; // Return null if not a new visitor and not forcing capture
  }
  
  // Get referrer information
  const referrer = document.referrer || '';
  let referrerDomain = '';
  
  if (referrer) {
    try {
      referrerDomain = new URL(referrer).hostname;
    } catch (e) {
      console.error('Error parsing referrer URL:', e);
    }
  }
  
  // Determine medium and source
  const urlParams = new URLSearchParams(window.location.search);
  const { source, medium } = determineSourceMedium(referrer, referrerDomain, urlParams);
  
  // Collect source information
  const sourceData = {
    uri: window.location.href,
    path: window.location.pathname,
    pageType: determinePageType(),
    referrer: referrer,
    timestamp: new Date().toISOString(),
    source: source,
    medium: medium,
    utmParams: {}
  };

  // Extract UTM parameters
  urlParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      sourceData.utmParams[key] = value;
    }
  });

  // attach the players status according to local storage
  const playerStatus = getPlayerStatus();
  sourceData.playerStatus = playerStatus;
  sourceData.sessionWasPlayer = playerStatus.userId ? 1 : 0;

  // Save to localStorage
  localStorage.setItem('gam-source', JSON.stringify(sourceData));
  
  //console.log('Source attribution data saved:', sourceData);
  return sourceData;
}

function determinePageType() {
  const pathname = window.location.pathname;
  if (pathname === '/') {
    return document.referrer?.includes('69i57.370j0j7') ? 'Homepage Unknown' : 'Homepage'; // indentifier for the weird referrer: https://www.google.com/search?q=free+slots&aqs=chrome..69i57.370j0j7&sourceid=chrome&ie=UTF-8
  } else if (pathname.startsWith("/free_fun_games.")) {
    return window.location.search?.includes('cid=gbclid') ? 'H5 with Tracking' : 'H5';
  } else if (pathname.startsWith('/blog/')) {
    return 'Blog';
  } else if (pathname.startsWith('/slots/')) {
    return 'Slots';
  } else if (pathname.startsWith('/reviews/')) {
    return 'Reviews';
  } else if (pathname.startsWith('/category/')) {
    return 'Category';
  } else if (pathname.startsWith('/lp/')) {
    return 'Affiliate';
  } else {
    return 'Other';
  }
}


/**
 * Determines the source and medium based on referrer and URL parameters
 * @param {string} referrer - The full referrer URL
 * @param {string} referrerDomain - The domain of the referrer
 * @param {URLSearchParams} urlParams - The URL search parameters
 * @returns {Object} Object containing source and medium values
 */
function determineSourceMedium(referrer, referrerDomain, urlParams) {
  // Known search engines domains and their names
  const searchEngines = {
    'google': 'Google',
    'bing': 'Bing',
    'yahoo': 'Yahoo',
    'duckduckgo': 'DuckDuckGo',
    'baidu': 'Baidu',
    'yandex': 'Yandex'
  };
  
  // Check for paid traffic
  for (const [param, source] of Object.entries(PAID_IDENTIFIERS)) {
    if (urlParams.has(param)) {
      return { source, medium: 'Paid' };
    }
  }
  
  // Check for UTM parameters (they take precedence if present)
  if (urlParams.has('utm_source') && urlParams.has('utm_medium')) {
    return {
      source: urlParams.get('utm_source'),
      medium: urlParams.get('utm_medium')
    };
  }
  
  // No referrer means direct traffic
  if (!referrer) {
    return { source: 'Direct', medium: 'Direct' };
  }
  
  // Check if referrer is a search engine
  for (const [searchDomain, searchName] of Object.entries(searchEngines)) {
    if (referrerDomain.includes(searchDomain)) {
      return { source: searchName, medium: 'Organic Search' };
    }
  }
  
  // If we have a referrer but it's not a search engine, it's a referral
  if (referrerDomain) {
    return { source: referrerDomain, medium: 'Referral' };
  }
  
  // Fallback
  return { source: 'Unassigned', medium: 'Unassigned' };
}
